import { Button } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { useEffect, useMemo, useState } from 'react';

import type { DatabaseImportParserProgress } from '../../../modules/streamlain/file-import/database-import-parser-worker-types';
import * as styles from './analysis-loading.css';

function progressLabel(
  phase: DatabaseImportParserProgress['phase'] | undefined,
  t: ReturnType<typeof useI18n>
): string {
  switch (phase) {
    case 'worker-starting':
      return t['com.streamlain.import.preview.analysis.phase.workerStarting']();
    case 'worker-ready':
      return t['com.streamlain.import.preview.analysis.phase.workerReady']();
    case 'file-reading':
      return t['com.streamlain.import.preview.analysis.phase.fileReading']();
    case 'csv-reading':
      return t['com.streamlain.import.preview.analysis.phase.csvReading']();
    case 'workbook-reading':
      return t[
        'com.streamlain.import.preview.analysis.phase.workbookReading'
      ]();
    case 'workbook-loaded':
      return t['com.streamlain.import.preview.analysis.phase.workbookLoaded']();
    case 'sheet-scanning':
      return t['com.streamlain.import.preview.analysis.phase.sheetScanning']();
    case 'preflight-complete':
      return t[
        'com.streamlain.import.preview.analysis.phase.preflightComplete'
      ]();
    case 'rows-extracting':
      return t['com.streamlain.import.preview.analysis.phase.rowsExtracting']();
    default:
      return t['com.streamlain.import.preview.analysis.phase.preparing']();
  }
}

export function ImportAnalysisLoading({
  title,
  progress,
  onCancel,
}: {
  title: string;
  progress: DatabaseImportParserProgress | null;
  onCancel: () => void;
}) {
  const t = useI18n();
  const [now, setNow] = useState(() => Date.now());
  const [startedAt] = useState(() => Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const subject = useMemo(() => {
    if (!progress?.fileName) return null;
    if (!progress.sheetName) return progress.fileName;
    return `${progress.fileName} / ${progress.sheetName}`;
  }, [progress?.fileName, progress?.sheetName]);
  const elapsedSeconds = Math.max(0, Math.floor((now - startedAt) / 1000));

  return (
    <div className={styles.root}>
      <div className={styles.title}>{title}</div>
      <div className={styles.detail}>{progressLabel(progress?.phase, t)}</div>
      {subject ? <div className={styles.subject}>{subject}</div> : null}
      <div className={styles.track} aria-hidden="true">
        <div className={styles.indicator} />
      </div>
      <div className={styles.elapsed}>
        {t['com.streamlain.import.preview.analysis.elapsed']({
          seconds: String(elapsedSeconds),
        })}
      </div>
      <div className={styles.actions}>
        <Button variant="secondary" onClick={onCancel}>
          {t['Cancel']()}
        </Button>
      </div>
    </div>
  );
}
